import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Zap, Target, Users, TrendingUp, Heart, Code, Mail } from "lucide-react";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { trackEvent } from "@/lib/analytics";

const About = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent('about_page_viewed');
  }, []);

  return (
    <div className="min-h-screen bg-background charcoal-pro">
      <MarketingHeader />

      {/* Hero */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
          <Code className="w-4 h-4" />
          Built by a solo developer
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
          About StudyForge
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Transforming focused work into an engaging progression system for learners worldwide.
        </p>
      </section>

      {/* Mission */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <Target className="w-12 h-12 text-primary" />
              <h2 className="text-3xl font-bold text-foreground">The Mission</h2>
            </div>
            <p className="text-lg text-muted-foreground leading-relaxed">
              I believe that learning should be engaging, measurable, and rewarding. StudyForge was born from a simple idea: 
              what if studying felt like playing a game? By gamifying the study experience with XP, streaks, leaderboards, and 
              achievements, I help students, professionals, and lifelong learners stay motivated and track their progress in 
              a way that's both fun and meaningful.
            </p>
          </Card>
        </div>
      </section>

      {/* Story */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">The Story</h2>
          <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
            <p>
              StudyForge started in 2024 as a personal project. I'm a developer who spent years trying Pomodoro apps, 
              habit trackers, and to-do lists — but nothing stuck. Every app felt like another chore, another thing 
              to check off.
            </p>
            <p>
              The breakthrough came when I asked myself: <span className="text-foreground font-medium">"What if studying felt like playing an RPG?"</span> What if every minute of focused work earned you XP, extended your streak, and moved you up a global leaderboard?
            </p>
            <p>
              So I built StudyForge for myself. Then friends asked to use it. Now early adopters worldwide are 
              tracking their study sessions, earning XP, and actually looking forward to studying.
            </p>
            <p className="text-foreground font-medium">
              I'm building this in public. Every feature is based on feedback from real users — and I personally 
              read every piece of feedback.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-secondary/30 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-foreground mb-12 text-center">Core Values</h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="p-6 text-center">
              <Users className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">Community First</h3>
              <p className="text-muted-foreground">
                Built by a learner, for learners. I listen to feedback and prioritize features that help the community thrive.
              </p>
            </Card>

            <Card className="p-6 text-center">
              <TrendingUp className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">Progress Over Perfection</h3>
              <p className="text-muted-foreground">
                Small, consistent steps lead to big results. I celebrate every session, no matter how short.
              </p>
            </Card>

            <Card className="p-6 text-center">
              <Heart className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">Mental Health Matters</h3>
              <p className="text-muted-foreground">
                I encourage balanced study habits with break recommendations and streak protection for busy days.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Get in Touch */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Mail className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-foreground mb-4">Want to Get in Touch?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              I'd love to hear your feedback! Use the in-app feedback form to share your thoughts, report bugs, 
              or suggest new features. I read every single message.
            </p>
            <Button onClick={() => {
              trackEvent('about_feedback_clicked');
              navigate('/auth');
            }}>
              Sign In to Send Feedback
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-secondary/30 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready to Join the StudyForge Community?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Start tracking your study sessions, earning XP, and climbing leaderboards today.
          </p>
          <Button size="lg" onClick={() => {
            trackEvent('footer_cta_clicked', { page: 'about' });
            navigate('/auth');
          }}>
            Get Started Free
          </Button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default About;
